import { useLocation, Link } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import type { DocNode, DocSection } from '~/lib/api'

interface DocsSidebarProps {
    sections: DocSection[]
}

export function DocsSidebar({ sections }: DocsSidebarProps) {
    const location = useLocation()
    const currentPath = location.pathname.replace('/docs/', '')

    // Defensive check for sections array
    if (!sections || !Array.isArray(sections)) {
        return (
            <nav className="h-full overflow-auto py-4 px-4">
                <div className="text-slate-400 text-sm">Loading navigation...</div>
            </nav>
        )
    }

    return (
        <nav className="h-full overflow-auto py-4">
            {sections.map((section) => (
                <SectionGroup key={section.id} section={section} currentPath={currentPath} />
            ))}
        </nav>
    )
}

function SectionGroup({ section, currentPath }: { section: DocSection; currentPath: string }) {
    const [isExpanded, setIsExpanded] = useState(true)

    // Defensive check
    const folders = section?.folders && Array.isArray(section.folders) ? section.folders : []

    return (
        <div className="mb-4">
            {/* Section Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-200 transition-colors"
            >
                <span>{section.icon}</span>
                <span className="flex-1 text-left">{section.title}</span>
                <span className={`transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}>
                    ▼
                </span>
            </button>

            {/* Section Content */}
            {isExpanded && folders.length > 0 && (
                <div className="space-y-1">
                    {folders.map((node) => (
                        node.type === 'dir' ? (
                            <FolderItem key={node.path} folder={node} currentPath={currentPath} />
                        ) : (
                            <div key={node.path} className="pl-4">
                                <DocItem doc={node} currentPath={currentPath} />
                            </div>
                        )
                    ))}
                </div>
            )}
        </div>
    )
}

function FolderItem({ folder, currentPath }: { folder: DocNode; currentPath: string }) {
    const hasActiveChild = folder.children?.some(doc =>
        currentPath === doc.path || currentPath === doc.path.replace('.md', '')
    )
    const [isExpanded, setIsExpanded] = useState(folder.primary || hasActiveChild || false)

    // Auto-expand if a child becomes active
    useEffect(() => {
        if (hasActiveChild) {
            setIsExpanded(true)
        }
    }, [hasActiveChild])

    const hasChildren = folder.children && folder.children.length > 0

    return (
        <div>
            {/* Folder Header */}
            <button
                onClick={() => hasChildren && setIsExpanded(!isExpanded)}
                className={`w-full flex items-center gap-2 px-4 py-2 hover:bg-slate-700/50 transition-colors ${folder.primary ? 'bg-blue-600/10 border-l-2 border-blue-500' : ''
                    }`}
            >
                <span className="text-sm">{folder.icon || '📁'}</span>
                <span className={`flex-1 text-left text-sm ${folder.primary ? 'text-blue-400 font-medium' : 'text-slate-200'}`}>
                    {folder.title || folder.name}
                </span>
                {hasChildren && (
                    <span className={`text-xs text-slate-500 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}>
                        ▼
                    </span>
                )}
            </button>

            {/* Folder Description */}
            {folder.description && isExpanded && (
                <p className="px-4 py-1 text-xs text-slate-500 leading-relaxed">
                    {folder.description}
                </p>
            )}

            {/* Folder Children (Docs) */}
            {isExpanded && hasChildren && (
                <div className="pl-4">
                    {folder.children?.map((doc) => (
                        <DocItem key={doc.path} doc={doc} currentPath={currentPath} />
                    ))}
                </div>
            )}
        </div>
    )
}

function DocItem({ doc, currentPath }: { doc: DocNode; currentPath: string }) {
    const isActive = currentPath === doc.path || currentPath === doc.path.replace('.md', '')

    return (
        <Link
            to="/docs/$"
            params={{ _splat: doc.path.replace('.md', '') }}
            className={`flex items-center gap-2 px-4 py-1.5 text-sm transition-colors ${isActive
                ? 'bg-blue-600/20 text-blue-400 border-l-2 border-blue-500 -ml-[2px]'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                }`}
        >
            <span className="text-xs">📄</span>
            <span className="truncate">{doc.title || doc.name.replace('.md', '')}</span>
        </Link>
    )
}
